import React from 'react';

import Error from 'next/error';
import Head from 'next/head';

import _get from 'lodash/get';

import { getScaledPrimaryImg } from '@/utilities';

import { withCtxMiddleware } from '@bonnet/next/ctx-middleware';
import { withCtxAbortOnStatusCode } from '@bonnet/next/ctx-middleware/with-ctx-abort-on-status-code';
import { withBonnetPage } from '@bonnet/next/page';
import { useRouterScroll } from '@bonnet/next/use-router-scroll';

import { pageNames } from '@atc/bonnet-paths';
import { useFeatures } from '@atc/features';
import { getScaledImageSource, getSrcSet } from '@atc/images';

import { breakpoints, imageSizes } from 'axl-config';

import offsetCalc from '@/utilities/offSetScrollCalculation';
import useHashScroll from '@/utilities/useHashScroll';
import usePauseAudioEye from '@/utilities/usePauseAudioEye';

import {
    ctxPresetFindCar,
    withCustomLeadRouting,
    withDataIslandDispatcher,
    withInterestRates,
    withWalletConfigs,
} from '@/context-middleware';

import {
    addVehicleDetailsPageData,
    dispatchVehicleDetailsPageData,
    withVDPExpired,
} from '@/context-middleware/vdp';
import withInventoryPageRedirect from '@/context-middleware/vdp/redirectToInventoryPage';

import {
    expiredListingDuck,
} from '@/ducks';

import { srpNewSearchDuck, srpResultsDuck, srpReuseActiveStateDuck } from '@/ducks/srp';
import * as vdpDucks from '@/ducks/vdp';

import {
    atcAuthModule,
    CanonicalUrlModule,
    CookieConsentModule,
    ParsedQueryModule,
    PersonalizationEngineModule,
    SearchLinkModule,
} from '@/modules';

import { CommonHeadContainer, PersonalizationEngineContainer } from '@/containers';

import { mainImageSizes } from '@/containers/vdp/MediaGallery/sizes';
import VDPAnalytics from '@/containers/vdp/VDPAnalytics';
import VdpPageEventsContainer from '@/containers/vdp/VdpPageEventsContainer';
import VehicleDetailsPageContainer from '@/containers/vdp/VehicleDetailsPageContainer';

import FordVehicleDetailsSchema from '@/components/FordVehicleDetailsSchema/index.js';

const { VEHICLE_DETAILS } = pageNames;

function VehicleDetailsPage({
    browser,
    currentUrl,
    images,
    statusCode,
    query,
}) {
    const {
        image_optimization: [enableImageOptimization],
        personalization_engine: [enablePersonalizationEngine],
    } = useFeatures([
        'image_optimization',
        'personalization_engine',
    ]);

    usePauseAudioEye();

    useRouterScroll();

    const scrollOffset = offsetCalc(20, 'sticky-header-vdp');
    useHashScroll({ enableSmoothScroll: true, offset: scrollOffset, delay: 750 });

    const primary = _get(images, 'primary', 0);
    const primaryImgSrc = _get(images, `sources[${primary}].src`, '');
    const verifiedImgSrc = enableImageOptimization ? primaryImgSrc.replace(/images.autotrader.com|assets.cai-media-management.com/, 'images2.autotrader.com') : primaryImgSrc;
    const scaledPrimaryImage = verifiedImgSrc && getScaledPrimaryImg(VEHICLE_DETAILS, browser, verifiedImgSrc, { isOptimized: enableImageOptimization });
    const sizeSet = [mainImageSizes.xs, mainImageSizes.sm, mainImageSizes.md, mainImageSizes.lg];
    const srcSet = getSrcSet(verifiedImgSrc, sizeSet, { isOptimized: enableImageOptimization });
    const openGraphImage = verifiedImgSrc && getScaledImageSource({
        src: verifiedImgSrc,
        isOptimized: enableImageOptimization,
        ...imageSizes.openGraphImg,
    });

    if (statusCode) {
        return <Error statusCode={statusCode} />;
    }

    return (
        <>
            {enablePersonalizationEngine && <PersonalizationEngineContainer pageName={VEHICLE_DETAILS} />}
            <CommonHeadContainer
                currentUrl={currentUrl}
                openGraphImage={openGraphImage}
                pageName={VEHICLE_DETAILS}
                query={query}
            />
            <Head>
                <link
                    rel="preload"
                    as="image"
                    href={scaledPrimaryImage}
                    imageSrcSet={srcSet}
                    imageSizes={`(min-width: ${breakpoints.min.lg}px) ${mainImageSizes.lg.width}px, (min-width: ${breakpoints.min.md}px) ${mainImageSizes.md.width}px, (min-width: ${breakpoints.min.sm}px) ${mainImageSizes.sm.width}px, ${mainImageSizes.xs.width}px`}
                />
            </Head>
            <FordVehicleDetailsSchema />
            <VdpPageEventsContainer />
            <VehicleDetailsPageContainer />
            <VDPAnalytics />
        </>
    );
}

VehicleDetailsPage.getInitialProps = async (ctx) => {

    if (srpResultsDuck.selectors.hasActiveResults(ctx.store.getState())) {
        ctx.store.dispatch(srpReuseActiveStateDuck.creators.setTrue());
    }

    ctx.store.dispatch(srpNewSearchDuck.creators.setFalse());

    await withCtxMiddleware([
        withInventoryPageRedirect(),
        ctxPresetFindCar({
            pageName: VEHICLE_DETAILS,
            preFetchCtxMiddleware: [
                ctx.withModuleCtxMiddleware({
                    batchDispatch: false,
                    fetchCompositeJSON: false,
                }),
            ],
            fetcher: addVehicleDetailsPageData,
            dispatcher: dispatchVehicleDetailsPageData,
        }),
        withVDPExpired(),
        withCtxAbortOnStatusCode(),
        withDataIslandDispatcher(),
        withInterestRates(),
        withWalletConfigs(),
        withCustomLeadRouting(),
    ], ctx);

    // if the current ctx contains a status code then we need to abort the process and return it
    // to allow the page to render an error
    if (ctx.statusCode) {
        return {
            statusCode: ctx.statusCode,
        };
    }
    return {
        currentUrl: ctx.data.currentUrl,
        images: _get(ctx, 'data.listings[0].images'),
        query: ctx.query,
        dataIsland: ctx.data.dataIsland,
        browser: ctx.store.getState().browser,
    };
};

const { BonnetPageComponent } = withBonnetPage(VehicleDetailsPage, {
    reducers: [
        ...Object.values(vdpDucks),
        srpResultsDuck,
        srpReuseActiveStateDuck,
        expiredListingDuck, // TODO: BONNET - should this be added in _app as it used on both?
    ],
    modules: [
        SearchLinkModule,
        CookieConsentModule,
        CanonicalUrlModule,
        ParsedQueryModule,
        PersonalizationEngineModule,
        atcAuthModule,
    ],
});

export default BonnetPageComponent;
